import axios from "axios";
import { format, formatISO, isSameDay, startOfDay } from "date-fns";
import { useEffect, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/edit";
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  TextFieldProps,
  useMediaQuery,
  useTheme,
  Tooltip
} from "@mui/material";
import Clear from "@mui/icons-material/Clear";
import Iconify from "../../minimals/components/iconify";
import { useCalendar } from "../contexts/CalendarContext";
import useDebounce from "../../hooks/useDebounce";
import OpenInHomePageButton from "../searches/OpenInHomePageButton";
import OpenInSelectButton from "../searches/OpenInSelectButton";
import { fCurrency } from "../utils/formatNumber";
import { fIsValidDate } from "../utils/formatTime";
import {
  compareCalendarEvents,
  getCalendarEventDate,
  getStatusColor,
  getStatusTitle,
  statusOptions
} from "./CalendarUtils";
import PropertyAddress from "./PropertyAddress";
import { logEvent } from "../utils/analyticsLogger";
import {
  getClosedOrder,
  getClosedOrderAdditionalInfo,
  getClosingCalendarFunderAndCloser,
  updateClosingInfo
} from "./CalendarActions";
import { ICalendarEvent, IContact } from "../types/calendar";
import { IOption, IGenericOption, InternalUser } from "../types/app";
import DotLoader from "../components/DotLoader";
import { DesktopDatePicker, TimePicker } from "@mui/x-date-pickers";
import { LoadingButton } from "@mui/lab";
import { convertInternalUserToContact } from "../orderEntry/utils/helpers";
import { useInternalUsers } from "../contexts/InternalUserContext";
import { useQueryParam, StringParam } from "use-query-params";

interface IContactOption extends IGenericOption<IContact> {}

interface IUpdateForm {
  orderStatus: IOption;
  accountRep: IOption;
  settlementDate: string | null;
  funder: { value: IContact | null; label: string } | null;
  titleCloser: { value: IContact | null; label: string } | null;
}

interface Props {
  getOrdersWithLoader: () => void;
  /** Provided only when the order was opened from the day list. */
  onBackToList?: () => void;
}

type OrderBundle = {
  order: Awaited<ReturnType<typeof getClosedOrder>>["data"];
  funderAndCloser: Awaited<ReturnType<typeof getClosingCalendarFunderAndCloser>>["data"];
  additionalInfo: Awaited<ReturnType<typeof getClosedOrderAdditionalInfo>>["data"];
};

const KEY_HINTS = [
  { keys: "\u2190 \u2192", label: "Previous / next closing" },
  { keys: "\u2191 \u2193", label: "Previous / next day with closings" },
  { keys: "Esc", label: "Close this card" }
];

/**
 * The three calls an order card needs. They don't depend on each other, so they
 * go out together instead of in series.
 */
const fetchOrderBundle = async (orderNumber: string): Promise<OrderBundle> => {
  const [order, funderAndCloser, additionalInfo] = await Promise.all([
    getClosedOrder(orderNumber),
    getClosingCalendarFunderAndCloser(orderNumber),
    getClosedOrderAdditionalInfo(orderNumber)
  ]);
  return { order: order.data, funderAndCloser: funderAndCloser.data, additionalInfo: additionalInfo.data };
};

export default function ViewClosingModal({ getOrdersWithLoader, onBackToList }: Props) {
  const { calendarEvents, modalOrder, setModalOrder, viewOrderMode: mode, setViewOrderMode: setMode } = useCalendar();
  const { accountRepOptions } = useInternalUsers();
  const theme = useTheme();
  // md, matching the rest of the calendar's mobile treatment.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!modalOrder) {
    return <></>;
  }

  const {
    salesPrice = "",
    orderStatus = "",
    accountRep = "",
    accountRepId = "",
    funder = "",
    clientName = "",
    titleCloser = "",
    propertyType = "",
    loanAmount = "",
    premium = "",
    salesRep = "",
    principal = "",
    buyerAttorney = "",
    sellerAttorney = "",
    fullOrderNumber = "",
    settlementDate,
    projectName = "",
    transactionType = "",
    identifier = ""
  } = modalOrder;

  const orderNumber = modalOrder.orderNumber || "";

  const [isOpenSettlementDatePicker, setIsOpenSettlementDatePicker] = useState(false);
  const [isOpenSettlementTimePicker, setIsOpenSettlementTimePicker] = useState(false);
  const [isLoadingAdditonalInfo, setIsLoadingAdditonalInfo] = useState(false);
  const [isNavigatingOrder, setIsNavigatingOrder] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lendersAttorney, setLendersAttorney] = useState("");
  const [properties, setProperties] = useState([]);
  const [funderOptions, setFunderOptions] = useState<IContactOption[]>([]);
  const [titleCloserOptions, setTitleCloserOptions] = useState<IContactOption[]>([]);
  const [orderNumberUrlParam, setOrderNumberUrlParam] = useQueryParam("ordernumber", StringParam);

  const [funderSearchInputValue, setFunderSearchInputValue] = useState(funder || "");
  const debouncedFunderSearchInput = useDebounce(funderSearchInputValue, 500);

  const [titleCloserSearchInputValue, setTitleCloserSearchInputValue] = useState(titleCloser || "");
  const debouncedTitleCloserSearchInput = useDebounce(titleCloserSearchInputValue, 500);
  // Keyed by order number, holding the promise rather than the result so a
  // prefetch still in flight is awaited instead of duplicated. Lives with the
  // modal, so reopening it always refetches.
  const orderBundleCache = useRef(new Map<string, Promise<OrderBundle>>());

  const loadOrderBundle = (targetOrderNumber: string) => {
    const cache = orderBundleCache.current;
    let pending = cache.get(targetOrderNumber);
    if (!pending) {
      pending = fetchOrderBundle(targetOrderNumber).catch((e) => {
        cache.delete(targetOrderNumber);
        throw e;
      });
      cache.set(targetOrderNumber, pending);
    }
    return pending;
  };

  const funderSearchRequestId = useRef(0);
  const titleCloserSearchRequestId = useRef(0);

  useEffect(() => {
    if (!debouncedFunderSearchInput) {
      return setFunderOptions([]);
    }

    const requestId = ++funderSearchRequestId.current;
    axios
      .get<InternalUser[]>(`/api/clientphonebook/internalUsers/searchFunders?searchText=${debouncedFunderSearchInput}`)
      .then(({ data }) => {
        if (requestId !== funderSearchRequestId.current) {
          return;
        }
        const options = data.map((d) => {
          const funderAsContact = convertInternalUserToContact(d);
          return {
            value: { ...funderAsContact, lookupCode: "" } as IContact,
            label: funderAsContact.name || ""
          };
        });
        setFunderOptions(options);
      });
  }, [debouncedFunderSearchInput]);

  useEffect(() => {
    const requestId = ++titleCloserSearchRequestId.current;
    if (!debouncedTitleCloserSearchInput) {
      setTitleCloserOptions([]);
      return;
    }

    axios
      .get<IContact[]>(`/proxy/api/clients/getbyrole?role=Title Closer&searchterm=${debouncedTitleCloserSearchInput}`)
      .then(({ data }) => {
        if (requestId !== titleCloserSearchRequestId.current) {
          return;
        }
        setTitleCloserOptions(data.map((d) => ({ value: d, label: d.name })));
      });
  }, [debouncedTitleCloserSearchInput]);

  const [updatedFormValues, setUpdatedFormValues] = useState<IUpdateForm>({
    orderStatus: { value: orderStatus || "", label: orderStatus || "" },
    accountRep: { value: accountRepId || "", label: accountRep || "" },
    settlementDate: settlementDate || "",
    funder: { value: null, label: "" },
    titleCloser: { value: null, label: "" }
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    logEvent("Closing Calendar", "Edit Closing", "");
    const { orderStatus, settlementDate, accountRep, funder, titleCloser } = updatedFormValues;
    if (!orderNumber || !orderStatus?.value || (settlementDate && !fIsValidDate(settlementDate))) {
      setIsSubmitting(false);
      return false;
    }

    const updatePayload = {
      orderNumber,
      settlementDate: settlementDate || null,
      status: orderStatus.value || "",
      accountRepId: accountRep?.value || null,
      funder: funder?.value || null,
      titleCloser: titleCloser?.value || null
    };

    try {
      await updateClosingInfo(updatePayload);
      orderBundleCache.current.delete(orderNumber);
      await refreshOrderData(orderNumber);

      setMode("view");
      getOrdersWithLoader();
      setIsSubmitting(false);
    } catch (e) {
      console.error("Error in order update submit", e);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setModalOrder(null);
    setOrderNumberUrlParam(null);
    // setIsEditModeParam(null);
  };

  const onClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (!isSubmitting && (reason == "backdropClick" || reason == "escapeKeyDown")) {
      handleClose();
    }
  };

  // Left/right page between the day's closings. On the Dialog rather than in an
  // effect: the focus trap means every keystroke bubbles here anyway, and it
  // avoids a listener that outlives the card.
  const handleDialogKeyDown = (event: React.KeyboardEvent) => {
    if (mode !== "view" || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const target = event.target as HTMLElement | null;
    // Don't steal the arrow keys from a field or a select.
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
      return;
    }
    // Left/right page the day's closings; up/down are the bigger jump, to another
    // day. Vertical for "bigger" avoids modifier combos that clash with the browser.
    if (event.key === "ArrowLeft" && previousOrder) {
      event.preventDefault();
      navigateToOrder(previousOrder);
    } else if (event.key === "ArrowRight" && nextOrder) {
      event.preventDefault();
      navigateToOrder(nextOrder);
    } else if (event.key === "ArrowUp" && previousDay) {
      event.preventDefault();
      navigateToDay(previousDay);
    } else if (event.key === "ArrowDown" && nextDay) {
      event.preventDefault();
      navigateToDay(nextDay);
    }
  };

  const refreshOrderData = async (orderNumber: string) => {
    const { order: orderData, funderAndCloser: data, additionalInfo } = await loadOrderBundle(orderNumber);
    setModalOrder(orderData);
    const orderStatus = orderData?.orderStatus || "";

    const funderName = data.funder?.name || "";
    const titleCloserName = data.titleCloser?.name || "";
    setFunderSearchInputValue(funderName);
    setTitleCloserSearchInputValue(titleCloserName);

    setLendersAttorney(additionalInfo?.lendersAttorney || "");
    setProperties(additionalInfo?.properties || "");

    setUpdatedFormValues({
      orderStatus: { value: orderStatus, label: orderStatus },
      accountRep: {
        value: orderData.accountRepId || "",
        label: orderData.accountRep || ""
      },
      settlementDate: orderData.settlementDate || "",
      funder: { value: data.funder, label: funderName },
      titleCloser: { value: data.titleCloser, label: titleCloserName }
    });
  };

  const currentEventDate = getCalendarEventDate(modalOrder);
  const sameDateEvents = currentEventDate
    ? calendarEvents
        .filter((event) => {
          const eventDate = getCalendarEventDate(event);
          return eventDate ? isSameDay(eventDate, currentEventDate) : false;
        })
        .sort(compareCalendarEvents)
    : [];
  const currentEventIndex = sameDateEvents.findIndex((event) => event.orderNumber === orderNumber);
  const previousOrder = currentEventIndex > 0 ? sameDateEvents[currentEventIndex - 1] : null;
  const nextOrder =
    currentEventIndex >= 0 && currentEventIndex < sameDateEvents.length - 1
      ? sameDateEvents[currentEventIndex + 1]
      : null;
  // Days that actually hold closings, so a day-jump never lands on an empty one.
  // Built from the events already loaded for the visible range, so it costs nothing.
  // Deliberately not useMemo: this sits below the component's `!modalOrder` early
  // return, so a hook here would change the hook count between renders.
  const daysWithEvents = (() => {
    const byDay = new Map<number, ICalendarEvent[]>();
    calendarEvents.forEach((event) => {
      const eventDate = getCalendarEventDate(event);
      if (!eventDate) {
        return;
      }
      const key = startOfDay(eventDate).getTime();
      byDay.set(key, [...(byDay.get(key) || []), event]);
    });
    return [...byDay.entries()]
      .sort(([a], [b]) => a - b)
      .map(([time, events]) => ({ time, events: [...events].sort(compareCalendarEvents) }));
  })();

  const currentDayIndex = currentEventDate
    ? daysWithEvents.findIndex((day) => day.time === startOfDay(currentEventDate).getTime())
    : -1;
  const previousDay = currentDayIndex > 0 ? daysWithEvents[currentDayIndex - 1] : null;
  const nextDay =
    currentDayIndex >= 0 && currentDayIndex < daysWithEvents.length - 1 ? daysWithEvents[currentDayIndex + 1] : null;

  const navigationDateLabel = currentEventDate ? format(currentEventDate, "EEE, MMM d, yyyy") : "";
  // Same signal the month grid uses for the current day, so red reads as "today"
  // across the calendar rather than as a warning.
  const isTodaysDate = currentEventDate ? isSameDay(currentEventDate, new Date()) : false;
  const TODAY_COLOR = "#FF4842";
  const navigationPositionLabel =
    currentEventIndex >= 0 && sameDateEvents.length > 0 ? `${currentEventIndex + 1}/${sameDateEvents.length}` : "";

  // Values that arrive with the order fetch. While the arrows are paging, the
  // state still holds the previous order's data, so show a placeholder rather
  // than the wrong number. Arrows are mobile-only, so desktop never sees this.
  const renderFieldValue = (value: React.ReactNode) => {
    if (isNavigatingOrder) {
      return <Skeleton variant="text" width="70%" sx={{ fontSize: "0.875rem" }} />;
    }
    // Mobile keeps every field in a fixed position so they don't move between
    // orders, so an empty one gets a placeholder rather than a blank gap.
    const isEmpty = value === null || value === undefined || value === "";
    return <DialogContentText variant="body2">{isEmpty && isMobile ? "--" : value}</DialogContentText>;
  };

  const showDesktopNav = !isMobile && mode === "view" && (sameDateEvents.length > 1 || daysWithEvents.length > 1);

  const navigateToDay = (day: { events: ICalendarEvent[] } | null) => {
    if (day?.events?.length) {
      navigateToOrder(day.events[0]);
    }
  };

  const navigateToOrder = async (targetOrder: ICalendarEvent | null) => {
    if (!targetOrder?.orderNumber || isNavigatingOrder) {
      return;
    }

    setIsNavigatingOrder(true);
    setMode("view");
    setModalOrder(targetOrder);
    setOrderNumberUrlParam(targetOrder.orderNumber);

    try {
      await refreshOrderData(targetOrder.orderNumber);
    } finally {
      setIsNavigatingOrder(false);
    }
  };

  const getAndSetAdditionalData = async (orderNumber: string) => {
    setIsLoadingAdditonalInfo(true);
    try {
      const [{ data }, { data: additionalInfo }] = await Promise.all([
        getClosingCalendarFunderAndCloser(orderNumber),
        getClosedOrderAdditionalInfo(orderNumber)
      ]);
      const funderName = data.funder?.name || "";
      const titleCloserName = data.titleCloser?.name || "";
      setUpdatedFormValues((formVals) => ({
        ...formVals,
        funder: { value: data.funder, label: funderName },
        titleCloser: { value: data.titleCloser, label: titleCloserName }
      }));

      setFunderSearchInputValue(funderName);
      setTitleCloserSearchInputValue(titleCloserName);

      setLendersAttorney(additionalInfo?.lendersAttorney || "");
      setProperties(additionalInfo?.properties || "");
      setIsLoadingAdditonalInfo(false);
    } catch (e) {
      console.error("Error in getting additonal order info update submit", e);
      alert("There was an issue getting all the info for this order. Changes cannot be submitted at this time");
    }
  };

  useEffect(() => {
    getAndSetAdditionalData(orderNumber);
    // refreshData(orderNumber);
    if (!orderNumberUrlParam) {
      setOrderNumberUrlParam(orderNumber);
    }
    return () => setModalOrder(null);
  }, []);

  // Warm the neighbours the arrows can reach, so paging renders from cache
  // instead of waiting on a round trip. Both layouts have arrows now.
  const previousOrderNumber = previousOrder?.orderNumber;
  const nextOrderNumber = nextOrder?.orderNumber;
  useEffect(() => {
    if (mode !== "view") {
      return;
    }
    [previousOrderNumber, nextOrderNumber].forEach((neighbour) => {
      if (neighbour) {
        loadOrderBundle(neighbour).catch(() => {
          // Prefetch only. A failure just means the arrow fetches on click.
        });
      }
    });
  }, [mode, previousOrderNumber, nextOrderNumber]);

  const handleChange = (val: IOption | null, field: string) => {
    setUpdatedFormValues({
      ...updatedFormValues,
      [field]: val || { value: null, label: "" }
    });
  };

  const handleSettlementDateChange = (val: string | null | undefined | Date) => {
    if (!val)
      return setUpdatedFormValues({
        ...updatedFormValues,
        settlementDate: null
      });

    setUpdatedFormValues({
      ...updatedFormValues,
      settlementDate: val ? formatISO(new Date(val)) : ""
    });
  };

  const handleDateChange = (val: Date | null) => {
    if (!updatedFormValues.settlementDate && val) {
      const formatted = formatISO(startOfDay(val));
      handleSettlementDateChange(formatted);
      return;
    }
    handleSettlementDateChange(val?.toDateString());
  };

  const handleDateClear = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    handleSettlementDateChange(null);
    setIsOpenSettlementDatePicker(false);
  };

  const handleTimeClear = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    if (updatedFormValues.settlementDate) {
      const formatted = formatISO(startOfDay(new Date(updatedFormValues.settlementDate)));
      handleSettlementDateChange(formatted);
      setIsOpenSettlementTimePicker(false);
      return;
    }
    handleSettlementDateChange("");
    setIsOpenSettlementTimePicker(false);
  };

  return (
    <Dialog
      open={Boolean(modalOrder)}
      onClose={onClose}
      onKeyDown={handleDialogKeyDown}
      // Sit the card on top of the fixed nav bar rather than centring it, so the
      // gap above the pill stays constant however tall the card is.
      sx={isMobile ? { "& .MuiDialog-container": { alignItems: "flex-end" } } : undefined}
      PaperProps={{
        sx: isMobile
          ? {
              bgcolor: "transparent",
              boxShadow: "none",
              overflow: "visible",
              // Nested to outrank two same-specificity rules that would otherwise win
              // on source order: the theme's `@media (max-width:600px) { margin: 16px }`
              // shorthand (which resets marginBottom, dropping the card onto the nav
              // bar) and MUI's own `.MuiDialog-paperScrollPaper` max-height.
              "&.MuiDialog-paper": {
                width: "calc(100% - 32px)",
                maxWidth: 480,
                margin: 2,
                // Keeps the card clear of the fixed nav bar below it.
                marginBottom: "calc(80px + env(safe-area-inset-bottom))",
                // Height follows the content; the cap is what keeps a tall card off
                // the nav bar. `%` of the dialog container, not vh, because iOS vh
                // ignores the browser chrome and would run the card under it.
                maxHeight: "calc(100% - 16px - 80px - env(safe-area-inset-bottom))"
              }
            }
          : showDesktopNav
            ? {
                // MUI's default cap assumes its own 32px margins; leave room for the
                // fixed nav pill as well.
                maxHeight: "calc(100% - 128px)"
              }
            : undefined
      }}
    >
      <Box
        sx={
          isMobile
            ? {
                bgcolor: "background.paper",
                borderRadius: 3,
                boxShadow: "0 18px 45px rgba(22, 28, 36, 0.24)",
                overflow: "hidden",
                flex: "1 1 auto",
                minHeight: 0,
                display: "flex",
                flexDirection: "column"
              }
            : undefined
        }
      >
        {/* This Stack sits between the paper and DialogContent, so it has to pass the
            paper's height cap down: without flex/minHeight it grows to fit its
            content and DialogContent never gets a height to scroll within. */}
        <Stack
          spacing={isMobile ? 0 : 4}
          paddingBottom={isMobile ? 2 : 4}
          sx={isMobile ? { flex: "1 1 auto", minHeight: 0 } : undefined}
        >
          <DialogTitle sx={{ p: isMobile ? 2 : undefined }}>
            <Box component="span" sx={{ display: isMobile ? "block" : "inline", pr: 11 }}>
              {fullOrderNumber}
              {identifier && (
                <Box
                  sx={{
                    display: "inline-block",
                    marginLeft: 2,
                    position: "relative",
                    top: 7,
                    left: -4
                  }}
                >
                  <OpenInSelectButton identifier={identifier} />
                </Box>
              )}
              {orderNumber && <OpenInHomePageButton orderNumber={orderNumber || undefined} />}
            </Box>
            {projectName && <div>{projectName}</div>}

            <Box
              sx={{
                color: getStatusColor(orderStatus)?.color,
                fontSize: isMobile ? 16 : undefined,
                fontWeight: isMobile ? 500 : undefined
              }}
            >
              {getStatusTitle({ status: orderStatus, date: settlementDate })}
            </Box>

            {mode == "view" && (
              // Close sits in the corner rather than the action row: it's the dialog
              // convention, and it stops costing a full row at the bottom.
              <Stack direction="row" sx={{ position: "absolute", right: isMobile ? 8 : 12, top: isMobile ? 8 : 12 }}>
                <IconButton onClick={() => setMode("edit")} aria-label="edit">
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleClose()} aria-label="close">
                  <CloseIcon />
                </IconButton>
              </Stack>
            )}
          </DialogTitle>
          <DialogContent
            sx={{
              px: isMobile ? 2 : undefined,
              overscrollBehavior: isMobile ? "contain" : undefined,
              minHeight: isMobile ? 0 : undefined
            }}
          >
            <Stack spacing={1}>
              <Stack>
                <Typography variant="subtitle2">
                  {propertyType} {transactionType}
                </Typography>
                {isLoadingAdditonalInfo ? (
                  <DotLoader />
                ) : isNavigatingOrder ? (
                  <Skeleton variant="text" width="60%" sx={{ fontSize: "0.875rem" }} />
                ) : (
                  <PropertyAddress properties={properties} />
                )}
              </Stack>

              <Grid
                container
                columnSpacing={1}
                rowSpacing={isMobile ? 2 : 3}
                sx={
                  isMobile
                    ? {
                        // 3-column grid (xs={4}), so the first row is items 1-3. Their
                        // rowSpacing padding sits directly under the address block and
                        // just doubles the gap the parent Stack already provides.
                        "& > .MuiGrid-item:nth-of-type(-n+3)": { paddingTop: 0 },
                        // Left-align column 1 with the address block instead of
                        // carrying columnSpacing's gutter.
                        marginLeft: 0,
                        width: "100%",
                        "& > .MuiGrid-item:nth-of-type(3n+1)": { paddingLeft: 0 }
                      }
                    : undefined
                }
              >
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="subtitle2">Sales Price</Typography>
                    {renderFieldValue(salesPrice ? fCurrency(salesPrice) : "")}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="subtitle2">Loan Amount</Typography>
                    {renderFieldValue(loanAmount ? fCurrency(loanAmount) : "")}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="subtitle2">Premium</Typography>
                    {renderFieldValue(premium ? fCurrency(premium) : "")}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="subtitle2">Client</Typography>
                    {renderFieldValue(clientName)}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="subtitle2">Principal</Typography>
                    {renderFieldValue(principal)}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="subtitle2">Sales Rep</Typography>
                    {renderFieldValue(salesRep)}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="subtitle2">Buyers Attorney</Typography>
                    {renderFieldValue(buyerAttorney)}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="subtitle2">Sellers Attorney</Typography>
                    {renderFieldValue(sellerAttorney)}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack>
                    <Typography variant="subtitle2">Lenders Attorney</Typography>
                    {isLoadingAdditonalInfo ? <DotLoader /> : renderFieldValue(lendersAttorney)}
                  </Stack>
                </Grid>

                {mode === "view" && (
                  <>
                    <Grid item xs={4}>
                      <Stack>
                        <Typography variant="subtitle2">Account Rep</Typography>
                        {renderFieldValue(accountRep)}
                      </Stack>
                    </Grid>
                    <Grid item xs={4}>
                      <Stack>
                        <Typography variant="subtitle2">Funder</Typography>
                        {renderFieldValue(funder)}
                      </Stack>
                    </Grid>
                    <Grid item xs={4}>
                      <Stack>
                        <Typography variant="subtitle2">Title Closer</Typography>
                        {renderFieldValue(titleCloser)}
                      </Stack>
                    </Grid>
                  </>
                )}

                {mode == "edit" && (
                  // Two fixed 200px columns are wider than a phone card, so mobile
                  // stacks them into one full-width column instead.
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={isMobile ? 2.5 : 3}
                    sx={{ paddingTop: isMobile ? "16px" : "35px", width: isMobile ? "100%" : undefined }}
                  >
                    <Stack spacing={isMobile ? 2.5 : 3} width={isMobile ? "100%" : "200px"}>
                      <Autocomplete
                        disableClearable
                        id="modal-status"
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        options={statusOptions}
                        value={updatedFormValues.orderStatus}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Status"
                            sx={{
                              input: {
                                ...getStatusColor(updatedFormValues.orderStatus.label)
                              }
                            }}
                          />
                        )}
                        onChange={(e, val) => handleChange(val, "orderStatus")}
                        disabled={isSubmitting}
                        renderOption={(props, option) => (
                          <li {...props} key={option.value} style={{ ...getStatusColor(option.label) }}>
                            {option.label}
                          </li>
                        )}
                      />{" "}
                      <Autocomplete
                        id="modal-account-rep"
                        filterOptions={createFilterOptions({ limit: 10 })}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        options={accountRepOptions}
                        value={updatedFormValues.accountRep}
                        renderInput={(params) => <TextField {...params} label="Account Rep" />}
                        onChange={(e, val) => handleChange(val, "accountRep")}
                        disabled={isSubmitting}
                      />
                      <Autocomplete
                        id="modal-title-closer"
                        isOptionEqualToValue={(option, value) => option.label === value.label}
                        filterOptions={(options) => options}
                        inputValue={titleCloserSearchInputValue}
                        options={titleCloserOptions}
                        value={updatedFormValues.titleCloser}
                        onInputChange={(e, val, reason) => {
                          if (reason === "input") {
                            setTitleCloserOptions([]);
                          }
                          if (reason === "input" || reason === "clear") {
                            setTitleCloserSearchInputValue(val);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Title Closer"
                            inputProps={{
                              ...params.inputProps,
                              autoComplete: "new-password"
                            }}
                          />
                        )}
                        onChange={(e, val) => {
                          setUpdatedFormValues({ ...updatedFormValues, titleCloser: val });
                          setTitleCloserSearchInputValue(val?.label || "");
                        }}
                        disabled={isSubmitting}
                      />
                    </Stack>

                    <Stack spacing={isMobile ? 2.5 : 3} width={isMobile ? "100%" : "200px"}>
                      <DesktopDatePicker
                        open={isOpenSettlementDatePicker}
                        onOpen={() => {
                          if (!isSubmitting) {
                            setIsOpenSettlementDatePicker(true);
                          }
                        }}
                        onClose={() => setIsOpenSettlementDatePicker(false)}
                        label="Settlement Date"
                        value={new Date(updatedFormValues.settlementDate || "")}
                        onChange={handleDateChange}
                        disabled={isSubmitting}
                        slotProps={{
                          textField: {
                            disabled: isSubmitting,
                            sx: { " .MuiOutlinedInput-input": { cursor: "pointer" } },
                            inputProps: { readOnly: true },
                            onClick: () => {
                              if (!isSubmitting) {
                                setIsOpenSettlementDatePicker(true);
                              }
                            },
                            error:
                              Boolean(updatedFormValues.settlementDate) &&
                              !fIsValidDate(updatedFormValues.settlementDate),
                            fullWidth: true,
                            InputProps: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={handleDateClear} disabled={isSubmitting}>
                                    <Clear />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                          }
                        }}
                      />
                      <TimePicker
                        open={isOpenSettlementTimePicker}
                        onOpen={() => {
                          if (!isSubmitting) {
                            setIsOpenSettlementTimePicker(true);
                          }
                        }}
                        onClose={() => setIsOpenSettlementTimePicker(false)}
                        label="Settlement Time"
                        value={new Date(updatedFormValues.settlementDate || "")}
                        onChange={handleSettlementDateChange}
                        disabled={isSubmitting}
                        slotProps={{
                          textField: {
                            disabled: isSubmitting,
                            sx: {
                              " .MuiOutlinedInput-input": { cursor: "pointer" }
                            },
                            inputProps: {
                              readOnly: true
                            },
                            onClick: () => {
                              if (!isSubmitting) {
                                setIsOpenSettlementTimePicker(true);
                              }
                            },
                            fullWidth: true,
                            InputProps: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={handleTimeClear} disabled={isSubmitting}>
                                    <Clear />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                          }
                        }}
                      />
                      <Autocomplete
                        disabled={isSubmitting}
                        id="modal-funder"
                        isOptionEqualToValue={(option, value) => option.label === value.label}
                        filterOptions={(options) => options}
                        inputValue={funderSearchInputValue}
                        options={funderOptions}
                        value={updatedFormValues.funder}
                        onInputChange={(e, val, reason) => {
                          if (reason === "input") {
                            setFunderOptions([]);
                          }
                          if (reason === "input" || reason === "clear") {
                            setFunderSearchInputValue(val);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Funder"
                            inputProps={{
                              ...params.inputProps,
                              autoComplete: "new-password"
                            }}
                          />
                        )}
                        onChange={(e, val) => {
                          setUpdatedFormValues({ ...updatedFormValues, funder: val });
                          setFunderSearchInputValue(val?.label || "");
                        }}
                      />
                    </Stack>
                  </Stack>
                )}
              </Grid>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  width: "100%",
                  // View mode closes from the title row; edit mode keeps this row
                  // for Cancel/Save.
                  display: mode === "view" ? "none" : "flex",
                  justifyContent: "flex-end",
                  alignItems: "center"
                }}
              >
                <Button variant="outlined" disabled={isSubmitting} onClick={() => handleClose()}>
                  {mode == "view" ? "Close" : "Cancel"}
                </Button>
                {mode == "edit" && (
                  <LoadingButton
                    loading={isSubmitting}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting || isLoadingAdditonalInfo}
                  >
                    Save
                  </LoadingButton>
                )}
              </Stack>
            </Stack>
          </DialogContent>
        </Stack>
      </Box>
      {/* Desktop's nav lives outside the card, pinned to the viewport like mobile's,
          so it stays put while the card's height changes between orders. */}
      {showDesktopNav && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 24,
            zIndex: 1,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none"
          }}
        >
          {/* Each arrow pair sits beside the thing it changes: the count pages through
              the day's closings, the date jumps to another day with closings. */}
          <Stack
            direction="row"
            alignItems="center"
            divider={<Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "divider", mx: 0.75 }} />}
            sx={{
              pointerEvents: "auto",
              px: 1,
              py: 0.5,
              borderRadius: 999,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              boxShadow: "0 10px 28px rgba(22, 28, 36, 0.22)"
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <IconButton
                size="small"
                aria-label="Previous closing"
                disabled={!previousOrder || isNavigatingOrder}
                onClick={() => navigateToOrder(previousOrder)}
                sx={{ color: "primary.main", visibility: previousOrder ? "visible" : "hidden" }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              {/* The position is the active value; the total is context, so it steps
                  back rather than competing with it. */}
              <Typography sx={{ fontSize: 14, whiteSpace: "nowrap", minWidth: 44, textAlign: "center" }}>
                <Box component="span" sx={{ color: "primary.dark", fontWeight: 700 }}>
                  {currentEventIndex + 1}
                </Box>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  /{sameDateEvents.length}
                </Box>
              </Typography>
              <IconButton
                size="small"
                aria-label="Next closing"
                disabled={!nextOrder || isNavigatingOrder}
                onClick={() => navigateToOrder(nextOrder)}
                sx={{ color: "primary.main", visibility: nextOrder ? "visible" : "hidden" }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                aria-label="Previous day with closings"
                disabled={!previousDay || isNavigatingOrder}
                onClick={() => navigateToDay(previousDay)}
                sx={{ color: "primary.main", visibility: previousDay ? "visible" : "hidden" }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography
                sx={{
                  color: isTodaysDate ? TODAY_COLOR : "primary.dark",
                  fontSize: 14,
                  fontWeight: 700,
                  whiteSpace: "nowrap"
                }}
              >
                {isTodaysDate ? `Today · ${navigationDateLabel}` : navigationDateLabel}
              </Typography>
              <IconButton
                aria-label="Next day with closings"
                disabled={!nextDay || isNavigatingOrder}
                onClick={() => navigateToDay(nextDay)}
                sx={{ color: "primary.main", visibility: nextDay ? "visible" : "hidden" }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>

            {/* The shortcuts aren't discoverable on their own, so the pill carries a
                legend rather than leaving people to find them by accident. */}
            <Tooltip
              placement="top"
              arrow
              componentsProps={{ tooltip: { sx: { maxWidth: 320, p: 1.5 } } }}
              title={
                <Stack spacing={1}>
                  {KEY_HINTS.map((hint) => (
                    <Stack key={hint.label} direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          flexShrink: 0,
                          minWidth: 44,
                          textAlign: "center",
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.75,
                          fontSize: 12,
                          fontWeight: 700,
                          border: "1px solid",
                          borderColor: "rgba(255, 255, 255, 0.4)"
                        }}
                      >
                        {hint.keys}
                      </Box>
                      <Typography variant="caption">{hint.label}</Typography>
                    </Stack>
                  ))}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 44,
                        display: "flex",
                        justifyContent: "center"
                      }}
                    >
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: TODAY_COLOR }} />
                    </Box>
                    <Typography variant="caption">Red marks today</Typography>
                  </Stack>
                </Stack>
              }
            >
              <IconButton size="small" aria-label="Keyboard shortcuts" sx={{ color: "text.disabled" }}>
                <Iconify icon="eva:question-mark-circle-outline" width={18} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      )}

      {isMobile && mode === "view" && sameDateEvents.length > 1 && (
        <Box
          sx={{
            // Pinned to the viewport, not to the card: card height varies with
            // content, so anchoring to it made the bar jump between orders.
            position: "fixed",
            left: 16,
            right: 16,
            bottom: "calc(16px + env(safe-area-inset-bottom))",
            maxWidth: 480,
            marginInline: "auto",
            zIndex: 1,
            px: 1,
            py: 0.75,
            borderRadius: 999,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 10px 28px rgba(22, 28, 36, 0.22)",
            // Equal 1fr sides keep the date group centred against the pill rather
            // than against whatever the back button and count leave over.
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: 1
          }}
        >
          <Stack direction="row" justifyContent="flex-start" sx={{ minWidth: 0 }}>
            {onBackToList && (
              <IconButton
                aria-label="Back to list"
                onClick={() => {
                  handleClose();
                  onBackToList();
                }}
                sx={{ color: "primary.main" }}
              >
                <FormatListBulletedIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" sx={{ gap: 0.5 }}>
            <IconButton
              aria-label="Previous closing"
              disabled={!previousOrder || isNavigatingOrder}
              onClick={() => navigateToOrder(previousOrder)}
              sx={{ color: "primary.main", "&.Mui-disabled": { color: "text.disabled" } }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              sx={{
                color: isTodaysDate ? TODAY_COLOR : "primary.dark",
                fontSize: 14,
                fontWeight: 700,
                lineHeight: 1.2,
                whiteSpace: "nowrap"
              }}
            >
              {navigationDateLabel}
            </Typography>
            <IconButton
              aria-label="Next closing"
              disabled={!nextOrder || isNavigatingOrder}
              onClick={() => navigateToOrder(nextOrder)}
              sx={{ color: "primary.main", "&.Mui-disabled": { color: "text.disabled" } }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" sx={{ minWidth: 0, pr: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
              {navigationPositionLabel}
            </Typography>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
